import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { DataRoomShareableLink } from 'models/ShareableLink';
import ReorderResizeTable from 'components/ReorderResizeTable';
import { BODY_CONTENT_GENERATE } from './components';
import {
  CUSTOM_COL_SIZES,
  HEADING_TRANSLATIONS,
  TABLE_COLUMNS,
  SORTING_VALUES,
} from './components/data';
import { Button } from 'ncoded-component-library';
import CreateShareableLinkContext from 'router/subrouters/DataRoom/providers/CreateShareableLink/CreateShareableLink.context';
import { useTranslation } from 'react-i18next';
import { isDateInNextDays } from 'dateUtils';

import './ShareableLinksTable.styles.scss';

type ShareableLinksTableProps = {
  className?: string;
  isLoading: boolean;
  totalPages: number;
  shareableLinks: DataRoomShareableLink[];
  showVisits?: boolean;
  updateShareableLink: (link: DataRoomShareableLink) => void;
  onRowClick: (link: DataRoomShareableLink) => void;
};

const ShareableLinksTable: React.FC<ShareableLinksTableProps> = (props) => {
  const {
    className,
    isLoading,
    totalPages,
    shareableLinks,
    showVisits = false,
    updateShareableLink,
    onRowClick,
  } = props;

  const { openCreateShareableLink } = useContext(CreateShareableLinkContext);

  const getRowBackgroundColor = useCallback((link: DataRoomShareableLink) => {
    const { isActive, linkExpiresDate } = link;

    return isActive && linkExpiresDate && isDateInNextDays(linkExpiresDate, 7)
      ? 'expires-soon-row'
      : '';
  }, []);

  const classes = classNames('shareable-links-table', className);

  const { t } = useTranslation();

  const renderedContent = useMemo(
    () =>
      TABLE_COLUMNS.reduce(
        (acc, col) => ({
          ...acc,
          [col]: (link: DataRoomShareableLink) =>
            BODY_CONTENT_GENERATE[col]({
              link,
              updateShareableLink,
            }),
        }),
        {},
      ),
    [updateShareableLink],
  );

  const headerColTransformation = useMemo(
    () => ({
      linkAccess: () => (
        <Button variant="outline" onClick={() => openCreateShareableLink()}>
          {t('createSecureLink')}
        </Button>
      ),
    }),
    [openCreateShareableLink, t],
  );

  return (
    <ReorderResizeTable
      className={classes}
      columns={TABLE_COLUMNS.filter(
        (el) => showVisits || el !== 'totalAccessCount',
      )}
      data={shareableLinks}
      isDragDisabled
      isResizeDisabled
      isDataLoading={isLoading}
      totalPages={totalPages}
      customSizes={CUSTOM_COL_SIZES}
      sortingValues={SORTING_VALUES}
      headingTranslations={HEADING_TRANSLATIONS}
      tableContentTransformations={renderedContent}
      tableHeaderContentTransformations={headerColTransformation}
      getRowBackgroundColor={getRowBackgroundColor}
      onRowClick={onRowClick}
    />
  );
};

export default ShareableLinksTable;
